import { extractPlaceholders } from './placeholders';

export { PLACEHOLDER_REGEX, extractPlaceholders } from './placeholders';

export const buildParameterKey = (component, key) => `${component}:${key}`;

export const getAllParametersFromForm = (form) => {
  const parameters = [];

  extractPlaceholders(form.header).forEach((key) => {
    parameters.push({ component: 'HEADER', key });
  });

  extractPlaceholders(form.body).forEach((key) => {
    parameters.push({ component: 'BODY', key });
  });

  return parameters;
};

export const normalizeTemplateParameters = (parameters) =>
  (parameters || []).map((parameter) => ({
    component: parameter.component,
    key: String(parameter.key),
  }));

export const resolveTemplateParameters = (response, form) => {
  const fromResponse = normalizeTemplateParameters(
    response?.configuration?.parameters || response?.parameters
  );

  if (fromResponse.length) {
    return fromResponse;
  }

  return getAllParametersFromForm(form);
};

export const shouldAssignTemplateParameters = (response, form) =>
  response?.operation_status === 'to_assign' &&
  resolveTemplateParameters(response, form).length > 0;

const buildExampleValues = (keys) => keys.map((key) => `Example ${key}`);

export const buildTemplateComponentsPayload = (form) => {
  const components = {};

  if (form.header?.trim()) {
    const headerKeys = extractPlaceholders(form.header);
    components.header = {
      text: form.header.trim(),
    };

    if (headerKeys.length) {
      components.header.example = {
        header_text: buildExampleValues(headerKeys),
      };
    }
  }

  if (form.body?.trim()) {
    const bodyKeys = extractPlaceholders(form.body);
    components.body = {
      text: form.body.trim(),
    };

    if (bodyKeys.length) {
      components.body.example = {
        body_text: [buildExampleValues(bodyKeys)],
      };
    }
  }

  if (form.footer?.trim()) {
    components.footer = {
      text: form.footer.trim(),
    };
  }

  return components;
};

export const TEMPLATE_NAME_REGEX = /^[a-z][a-z0-9_]*$/;

export const normalizeTemplateName = (name) => {
  if (!name?.trim()) {
    return '';
  }

  return name
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

export const isTemplateNameValid = (name) => {
  const normalized = normalizeTemplateName(name);
  return Boolean(normalized && TEMPLATE_NAME_REGEX.test(normalized));
};

export const buildCreateTemplatePayload = (form) => {
  const components = buildTemplateComponentsPayload(form);

  return {
    name: normalizeTemplateName(form.name),
    language: form.language,
    category: form.category,
    components,
    parameters: [],
  };
};

export const buildEditTemplatePayload = (form) => ({
  components: buildTemplateComponentsPayload(form),
});

export const getComponentText = (template, type) =>
  template?.components?.find((component) => component.type === type)?.text ??
  '';

export const mapParametersToBindings = (parameters) => {
  const parameterBindings = {};

  (parameters || []).forEach((parameter) => {
    if (parameter.variable_id) {
      parameterBindings[buildParameterKey(parameter.component, parameter.key)] =
        parameter.variable_id;
    }
  });

  return parameterBindings;
};

export const mapTemplateToForm = (template) => {
  const parameterBindings = mapParametersToBindings(
    template?.configuration?.parameters
  );

  return {
    name: template?.name || '',
    category: template?.category || 'UTILITY',
    language: template?.language || 'es_AR',
    header: getComponentText(template, 'HEADER'),
    body: getComponentText(template, 'BODY'),
    footer: getComponentText(template, 'FOOTER'),
    parameterBindings,
  };
};

export const isEditTemplateFormValid = (form) => {
  const body = form.body?.trim();

  if (!body) {
    return false;
  }

  if (extractPlaceholders(form.footer).length > 0) {
    return false;
  }

  return true;
};

export const isCreateTemplateFormValid = (form) => {
  const body = form.body?.trim();

  if (!isTemplateNameValid(form.name) || !body) {
    return false;
  }

  if (extractPlaceholders(form.footer).length > 0) {
    return false;
  }

  return true;
};

export const areParameterBindingsComplete = (parameters, bindings) =>
  (parameters || []).every((parameter) => {
    const key = buildParameterKey(parameter.component, parameter.key);
    return Boolean(bindings?.[key]);
  });

export const getTemplateBodyPreview = (template) => {
  const body = template?.components?.find(
    (component) => component.type === 'BODY'
  );
  return body?.text?.slice(0, 120) ?? '';
};

export const buildTemplatePreviewText = (form) => {
  const sections = [];

  if (form.header?.trim()) {
    sections.push(form.header.trim());
  }

  if (form.body?.trim()) {
    sections.push(form.body.trim());
  }

  if (form.footer?.trim()) {
    sections.push(form.footer.trim());
  }

  return sections.join('\n\n');
};

export const getTemplatePreviewParts = (template, form = {}) => ({
  header: getComponentText(template, 'HEADER') || form.header || '',
  body: getComponentText(template, 'BODY') || form.body || '',
  footer: getComponentText(template, 'FOOTER') || form.footer || '',
});

const STATUS_BADGE_VARIANTS = {
  TO_ASSIGN: 'warning',
  PENDING: 'info',
  REVIEWING: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  PAUSED: 'muted',
  DISABLED: 'muted',
  DELETED: 'muted',
};

export const getStatusBadgeVariant = (status) =>
  STATUS_BADGE_VARIANTS[status] ?? 'muted';

export {
  validateTemplateMetaRules,
  hasTemplateValidationErrors,
  isTemplateOperationValidationError,
  normalizeTemplateValidationErrors,
  TEMPLATE_META_VALIDATION_CODES,
} from './validation';
